import React, { useState, useEffect } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CreditCard, AlertCircle, Lock, Loader } from 'lucide-react';

const StripeCheckout = ({ amount, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isElementReady, setIsElementReady] = useState(false);

  // Check if PaymentElement is ready
  useEffect(() => {
    if (elements) {
      // PaymentElement fires 'ready' when fully loaded
      const paymentElement = elements.getElement('payment');
      if (paymentElement) {
        setIsElementReady(true);
      }
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setErrorMessage('Payment system not ready. Please wait a moment and try again.');
      return;
    }

    // Double-check PaymentElement is mounted
    const paymentElement = elements.getElement('payment');
    if (!paymentElement) {
      setErrorMessage('Payment form not loaded. Please refresh the page and try again.');
      if (onError) onError(new Error('Payment Element not mounted'));
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // First, validate the form
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message);
        if (onError) onError(submitError);
        setIsProcessing(false);
        return;
      }

      // Then confirm the payment
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/complete`,
        },
        redirect: 'if_required',
      });

      if (error) {
        // Handle "already succeeded" errors - payment went through
        if (error.code === 'payment_intent_unexpected_state' && 
            error.payment_intent?.status === 'succeeded') {
          console.log('Payment already succeeded, continuing...');
          if (onSuccess) onSuccess(error.payment_intent);
          return;
        }
        setErrorMessage(error.message);
        if (onError) onError(error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        if (onSuccess) onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
      if (onError) onError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler for when PaymentElement is ready
  const handleElementReady = () => {
    setIsElementReady(true);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <CreditCard className="text-blue-600 mr-2" size={24} />
            <h3 className="text-lg font-semibold">Payment Details</h3>
          </div>
          <div className="flex items-center text-xs text-gray-500">
            <Lock size={14} className="mr-1" />
            Secure Payment
          </div>
        </div>

        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="text-red-600 mr-2 flex-shrink-0" size={20} />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        {/* Loading indicator while Stripe loads */}
        {!isElementReady && (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <Loader className="animate-spin mr-2" size={20} />
            <span>Loading payment form...</span>
          </div>
        )}

        <div className={!isElementReady ? 'opacity-0 h-0 overflow-hidden' : ''}>
          <PaymentElement 
            onReady={handleElementReady}
            options={{
              layout: 'tabs',
              defaultValues: {
                billingDetails: {
                  address: {
                    country: 'US',
                  }
                }
              }
            }}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || !isElementReady || isProcessing}
        className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        {isProcessing ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
        ) : !isElementReady ? (
          'Loading...'
        ) : (
          `Pay $${amount.toFixed(2)}`
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        Your payment information is encrypted and secure. We never store your card details.
      </p>
    </form>
  );
};

export default StripeCheckout;